/*
 * sticky-atc-sync.js — PDP variant sync between the in-page product form and the
 * sticky Add-to-Cart bar (GitHub issue #111).
 *
 * Additive and isolated, following the assets/atc-guard.js precedent. It does NOT
 * modify assets/theme.js (a minified bundle with no source in this repo) and does
 * not re-implement any of the theme's variant logic.
 *
 * ROOT CAUSE
 * ----------
 * sections/product.liquid renders <product-form> twice for the same product: once
 * in the main column and once inside snippets/add-to-cart-sticky.liquid. Each render
 * carries its own [data-option-position] swatch set, its own input[name="id"], its
 * own [data-product-json] and its own button[name="add"].
 *
 * The theme's ProductForm custom element scopes itself to
 * `this.container.closest('[data-section-id]')` (its `outerWrapper`). `.product-section`
 * and `.sticky-cart-wrap` each carry `data-section-id`, and the sticky wrapper is a
 * sibling of `.product-section`, not a descendant. So the two instances have disjoint
 * outerWrappers and neither can see the other's inputs. A selection in one form updates
 * only that form. Net effect: the sticky bar can submit a variant the customer never
 * selected, and stays enabled when the selected variant is sold out.
 *
 * A second, independent defect makes the sticky label lie even about its OWN selection:
 * ProductForm.updateLegend() looks for `[data-option-value]` under
 * `this.container.parentNode`. In the main column <product-form>'s parent is
 * `.product__inner`, which also holds the variant block, so it resolves. In the sticky
 * bar <product-form> sits in `.prod-sticky-col-3` while the swatches are in
 * `.prod-sticky-col-2`, so the query finds nothing and the colour label never changes.
 *
 * FIX MECHANISM
 * -------------
 * One delegated `change` listener on document. When an `options[...]` input changes
 * inside one product scope, the same selection is replayed into every sibling scope for
 * the same product handle by setting that scope's own inputs and dispatching a single
 * `change` on one of them. That is exactly the signal ProductFormReader binds to, so the
 * mirrored ProductForm runs its normal pushState(): variant id input, Add to Cart
 * enable/disable and label, prices, sold-out swatch classes, `theme:variant:change`.
 * No theme internals are called and no state is duplicated: each form still computes
 * its own truth from its own inputs.
 *
 * LOOP GUARD
 * ----------
 * `syncing` is set for the duration of the replay. Replay is fully synchronous
 * (setting .checked plus dispatchEvent), so any `change` the replay produces bubbles to
 * this listener while the flag is still set and is ignored. The flag is cleared in a
 * `finally`, so a throw inside the theme's handler cannot wedge the sync off.
 *
 * IMAGE SCROLL
 * ------------
 * ProductForm.scrollImageViewport() does a `window.scrollTo` toward the newly selected
 * media when the slideshow carries `data-vertical-images`. That is fine when the customer
 * is looking at the gallery, but a mirrored update fired from the sticky bar (which only
 * appears once the customer has scrolled past the gallery) would yank the page back up.
 * The attribute is removed from the target scope's slideshow for the duration of the
 * replay and restored immediately after: the theme's own early-return path, no gallery
 * code touched. `theme:image:change` still fires, so the target gallery still changes
 * image (issue #112 covers gallery/arrow behaviour separately).
 */
(function () {
  'use strict';

  var SCOPE = '[data-section-type="product"][data-product-handle]';
  var STICKY = '.sticky-cart-wrap';
  var QUICKVIEW = '[data-quickview-modal]';
  var OPTION_INPUT = '[name^="options"]';
  var OPTION_WRAPPER = '[data-option-position]';
  var OPTION_LABEL = '[data-option-value]';
  var SLIDESHOW = '[data-product-slideshow][data-vertical-images]';

  var syncing = false;

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  function isOptionInput(el) {
    return !!(el && typeof el.name === 'string' && el.name.indexOf('options[') === 0);
  }

  // Every product scope on the page for this handle, excluding quickview drawers
  // (they are separate products with their own ProductForm) and scopes with no form.
  // Only returns a group when the sticky bar is one of them: this fix is scoped to
  // issue #111 and must not change behaviour on pages without a sticky ATC.
  function scopeGroup(scope) {
    var handle = scope.getAttribute('data-product-handle');
    if (!handle) return [];

    var group = toArray(document.querySelectorAll(SCOPE)).filter(function (el) {
      return el.getAttribute('data-product-handle') === handle &&
        !el.closest(QUICKVIEW) &&
        el.querySelector('product-form');
    });

    if (group.length < 2) return [];
    var hasSticky = group.some(function (el) { return el.matches(STICKY); });
    return hasSticky ? group : [];
  }

  // Currently selected option values in a scope, as [{name, value}].
  function readSelection(scope) {
    var selection = [];
    toArray(scope.querySelectorAll(OPTION_INPUT)).forEach(function (el) {
      if (el.type === 'radio' || el.type === 'checkbox') {
        if (el.checked) selection.push({ name: el.name, value: el.value });
      } else {
        selection.push({ name: el.name, value: el.value });
      }
    });
    return selection;
  }

  // The theme's PopoutSelect listens for `theme:popout:click` on its option anchors and
  // handles input value, toggle text, aria-current and the change dispatch itself.
  function popoutOptionFor(input, value) {
    var popout = input.closest('[data-popout]');
    if (!popout) return null;
    return toArray(popout.querySelectorAll('[data-popout-option]')).filter(function (a) {
      return a.getAttribute('data-value') === value;
    })[0] || null;
  }

  // Replay `selection` into `scope`. Returns true if anything was applied.
  function applySelection(scope, selection) {
    var inputs = toArray(scope.querySelectorAll(OPTION_INPUT));
    var changed = null;
    var popouts = [];

    selection.forEach(function (sel) {
      var group = inputs.filter(function (el) { return el.name === sel.name; });
      if (!group.length) return;

      var radios = group.filter(function (el) { return el.type === 'radio'; });
      if (radios.length) {
        var match = radios.filter(function (el) { return el.value === sel.value; })[0];
        if (!match || match.checked) return; // value not offered here, or already correct
        radios.forEach(function (el) { el.checked = false; });
        match.checked = true;
        changed = match;
        return;
      }

      var input = group[0];
      if (input.value === sel.value) return;

      if (input.hasAttribute('data-popout-input')) {
        var option = popoutOptionFor(input, sel.value);
        if (option) { popouts.push(option); return; }
      }
      input.value = sel.value;
      changed = input;
    });

    popouts.forEach(function (option) {
      option.dispatchEvent(new CustomEvent('theme:popout:click', {
        cancelable: true,
        bubbles: true,
        detail: { preventTrigger: true }
      }));
    });

    // One dispatch is enough: ProductFormReader re-reads every option input on any
    // change, so the mirrored form recomputes its complete state from this signal.
    if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));

    return !!(changed || popouts.length);
  }

  function withoutImageScroll(scope, fn) {
    var slideshows = toArray(scope.querySelectorAll(SLIDESHOW));
    slideshows.forEach(function (el) { el.removeAttribute('data-vertical-images'); });
    try {
      fn();
    } finally {
      slideshows.forEach(function (el) { el.setAttribute('data-vertical-images', ''); });
    }
  }

  // Rewrite the visible colour/size label in a scope from that scope's own inputs.
  // Needed because ProductForm.updateLegend() cannot reach the sticky bar's labels
  // (see the header note); harmless where the theme already got it right.
  function refreshLabels(root) {
    toArray(root.querySelectorAll(OPTION_LABEL)).forEach(function (label) {
      var wrapper = label.closest(OPTION_WRAPPER);
      if (!wrapper) return;

      var value = null;
      var checked = wrapper.querySelector('input[type="radio"]:checked');
      if (checked) {
        value = checked.value;
      } else {
        var input = wrapper.querySelector('[data-popout-input], [data-single-option-selector]');
        if (input) value = input.value;
      }

      if (value !== null && label.textContent.trim() !== value) {
        label.textContent = value;
      }
    });
  }

  /*
   * Capture phase, not bubble (raised by Codex review on PR #115, verified in theme.js).
   *
   * Swatch radios emit a native, bubbling `change`. But when an option renders as a
   * dropdown, `PopoutSelect.popupOptionsClick()` fires `new Event("change")` on the hidden
   * `[data-popout-input]` with no `bubbles`, so it would never reach a bubble-phase
   * listener on document and dropdown selections would not sync. The capture phase
   * traverses the ancestor chain for every dispatched event regardless of `bubbles`, so
   * one capture listener covers both input styles.
   *
   * Consequence of capture: this runs BEFORE the source form's own ProductForm handler
   * (which is bound to the input element, i.e. target phase). That is safe, because the
   * replay reads the raw inputs, whose values and checkedness are already final when the
   * event is dispatched. It never depends on the source ProductForm having recomputed.
   */
  document.addEventListener('change', function (event) {
    if (syncing) return;

    var input = event.target;
    if (!isOptionInput(input) || !input.closest) return;
    if (input.closest(QUICKVIEW)) return;

    var source = input.closest(SCOPE);
    if (!source) return;

    var group = scopeGroup(source);
    if (!group.length) return;

    var selection = readSelection(source);

    syncing = true;
    try {
      group.forEach(function (scope) {
        if (scope === source) return;
        withoutImageScroll(scope, function () {
          applySelection(scope, selection);
        });
      });
    } finally {
      syncing = false;
    }

    group.forEach(refreshLabels);
  }, true);

  /*
   * Swatch hover fallback (raised by Codex review on PR #115, confirmed in theme.js).
   *
   * The theme's `Swapper` caches the label's initial innerHTML in `deafaultContent` when
   * the section loads, previews the hovered value on `mouseenter`, restores the cache on
   * `mouseleave`, and re-caches ONLY when a `[data-swapper-hover]` label is clicked.
   * A mirrored selection is not a click, so without this the cache stays stale and the
   * first hover-and-leave in the mirrored form would put the old colour name back, which
   * is precisely the misleading label #111 is about. (The same hole already exists for
   * keyboard arrow selection on the swatch radios; this covers that too.)
   *
   * The Swapper instances are held in a module-private map inside the minified bundle and
   * cannot be reached to update the cache. Instead the label is re-asserted from the
   * scope's own checked input immediately after the restore. Registering the `mouseleave`
   * handler at `mouseover` time (rather than up front) guarantees it runs AFTER Swapper's,
   * regardless of which script initialised first. `once` keeps it from stacking. The
   * hover preview itself is untouched.
   */
  document.addEventListener('mouseover', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var hover = target.closest('[data-swapper-hover]');
    if (!hover || hover.__stickySyncHover) return;

    var wrapper = hover.closest(OPTION_WRAPPER);
    var scope = hover.closest(SCOPE);
    if (!wrapper || !scope || hover.closest(QUICKVIEW)) return;
    if (!scopeGroup(scope).length) return;

    hover.__stickySyncHover = true;
    hover.addEventListener('mouseleave', function () {
      hover.__stickySyncHover = false;
      refreshLabels(wrapper);
    }, { once: true });
  });
})();
